import { useCallback, useEffect, useMemo } from 'react';
import { useCustomLocale } from './useCustomLocale';
import { useDispatch, useSelector } from 'react-redux';
import { ICartItem, IRedux } from '../_store/types';
import productService from '../_service/product.service';
import { useQuery } from '@tanstack/react-query';
import { ORDER_TYPE_IDS } from '@/_constants';
import { IAutoAddingProduct } from '../_types';
import {
  ADD_TO_CART,
  PATCH_CART_ITEM,
  REMOVE,
} from '../_store/cart/cart.slice';
import { useActiveBrand } from './useActiveBrand';
import { BRAND_NAMES } from '../_utils/Constants';
import {
  AutoAddCartSyncAction,
  buildAutoAddItemsPayload,
  hasAutoAddCartSyncActions,
  planAutoAddCartSync,
} from '../_utils/autoAddCartSync';

export default function useAutoAddingProduct({
  cart,
  selectedShipperId,
  enabled = true,
}: {
  cart: ICartItem[];
  selectedShipperId?: string;
  enabled?: boolean;
}) {
  const currentLocale = useCustomLocale();
  const dispatch = useDispatch();
  const activeShipperId = useActiveBrand();
  const shipperId = selectedShipperId || activeShipperId;
  const currentBrandName = BRAND_NAMES?.[shipperId];

  const session = useSelector((state: IRedux) => state.session);
  const { is_hall, order_type } = session;

  const cartItems = useMemo(() => buildAutoAddItemsPayload(cart), [cart]);

  const { data: autoAddProductData, isLoading } = useQuery({
    queryKey: [
      'auto-add-product',
      order_type?.id,
      is_hall,
      cartItems,
      shipperId,
    ],
    queryFn: () =>
      productService.getAutoAddProduct(
        {
          Shipper: shipperId,
          page: 1,
          limit: 10,
          order_type_id:
            is_hall === 'true' ? ORDER_TYPE_IDS.hall : order_type?.id,
          is_active: true,
          items: JSON.stringify(cartItems),
        },
        shipperId
      ),
    enabled: Boolean(shipperId) && enabled,
    select: (res) => ({
      count: res?.data?.count,
      defaultProducts: res?.data?.products?.filter(
        (item) => !item?.need_recommend
      ),
      needRecommendProducts: res?.data?.products?.filter(
        (item) => item?.need_recommend === true
      ),
    }),
  });

  const { defaultProducts, needRecommendProducts } = autoAddProductData || {};

  const addAutoProductHandler = useCallback(
    (product: IAutoAddingProduct, isDefault = true) => {
      dispatch(
        ADD_TO_CART({
          image: product?.product?.image || '',
          brand: currentBrandName,
          key: '',
          comment: '',
          in_stop: false,
          discount_percent: 0,
          category_id: '',
          is_optional: !!product?.is_optional,
          price: product?.product?.out_price || 0,
          price_with_discount: product?.product?.out_price || 0,
          product_id: product?.product_id,
          type: product?.product?.type,
          variants: {},
          order_modifiers: [],
          quantity: product?.min_qty,
          min_qty: product?.min_qty,
          max_qty: product?.max_qty,
          default_product: isDefault,
          category_name: product?.product?.slug,
          product_name: product?.product?.title?.[currentLocale],
          shipper_id: shipperId,
          source_id: '',
          branch_id: '',
          menu_id: '',
        })
      );
    },
    [currentBrandName, currentLocale, dispatch, shipperId]
  );

  const applyAutoAddActions = useCallback(
    (actions: AutoAddCartSyncAction[]) => {
      actions.forEach((action) => {
        switch (action.type) {
          case 'add':
            addAutoProductHandler(action.product, action.isDefault);
            break;
          case 'patch':
            dispatch(
              PATCH_CART_ITEM({
                shipper_id: shipperId,
                key: action.key,
                ...action.patch,
              })
            );
            break;
          case 'remove':
            dispatch(REMOVE({ shipper_id: shipperId, key: action.key }));
            break;
        }
      });
    },
    [addAutoProductHandler, dispatch, shipperId]
  );

  useEffect(() => {
    if (isLoading || !cart?.length) return;

    const actions = planAutoAddCartSync(
      cart,
      defaultProducts,
      needRecommendProducts
    );
    if (!hasAutoAddCartSyncActions(actions)) return;

    applyAutoAddActions(actions);
    // Sync when auto-add API payload changes; cart is read from the current render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyAutoAddActions, defaultProducts, isLoading, needRecommendProducts]);

  const productIds = useMemo(() => {
    const needRecommends = needRecommendProducts || [];
    return String([
      ...cart?.map((product) => product.product_id),
      ...needRecommends?.map((product) => product?.product_id),
    ]);
  }, [cart, needRecommendProducts]);

  const recommendProducts = useMemo(
    () =>
      needRecommendProducts?.filter(
        (item) =>
          !cart?.some((cartItem) => cartItem.product_id === item.product_id)
      ),
    [cart, needRecommendProducts]
  );

  return { productIds, recommendProducts };
}
